import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTable } from 'spacetimedb/react';
import { tables } from '@vidmakr/bindings';
import { ArrowLeft, Share2 } from 'lucide-react';
import { AssetReviewOverview } from '@/components/AssetReviewOverview';
import { AssetReviewPanel } from '@/components/AssetReview';
import { ShareAssetDialog } from '@/components/ReviewLinks';
import { Button, EmptyState, PageHeader, Spinner } from '@/components/ui';
import { useSpaces } from '@/lib/spaces';

/**
 * The review of one media file as a page of its own: the team's decision,
 * every link's comments on the scrubber, the rounds and the conversations
 * (the same overview as the share dialog's, with room to work).
 */
export function AssetReviewPage() {
  const { orgId, assetId } = useParams();
  const [rows, ready] = useTable(tables.asset.where((a) => a.id.eq(assetId ?? '')));
  const [links] = useTable(tables.reviewLink.where((l) => l.assetId.eq(assetId ?? '')));
  const sp = useSpaces(orgId);
  const [sharing, setSharing] = useState(false);
  const asset = rows[0];
  const back = `/orgs/${orgId}/assets`;
  if (!ready) {
    return (
      <div className="flex h-64 items-center justify-center text-muted">
        <Spinner size={22} />
      </div>
    );
  }
  if (!asset) {
    return (
      <EmptyState title="File not found">
        It was deleted, or you no longer have access. <Link to={`/orgs/${orgId}/assets`} className="text-accent-2 hover:underline">Back to the asset library</Link>
      </EmptyState>
    );
  }
  const reviewable = asset.status === 'ready' && ['video', 'audio', 'image'].includes(asset.kind);
  const canShare = sp.canIn(asset.scopeId, 'editor');
  return (
    <div data-asset-review-page={asset.id}>
      <PageHeader
        title={
          <span className="flex min-w-0 items-center gap-2">
            <Link to={back} className="rounded-md p-1 text-muted hover:bg-panel-3 hover:text-fg" aria-label="Back to the asset library" title="Back to the asset library">
              <ArrowLeft size={16} />
            </Link>
            <span className="min-w-0 truncate" title={asset.name}>
              {asset.name}
            </span>
          </span>
        }
        subtitle={`Review · ${links.length} link${links.length === 1 ? '' : 's'}`}
        actions={
          canShare && reviewable ? (
            <Button variant="primary" onClick={() => setSharing(true)} data-asset-review-page-share>
              <Share2 size={15} /> Manage links
            </Button>
          ) : null
        }
      />
      {reviewable ? (
        <div className="flex flex-col gap-4">
          <AssetReviewPanel asset={asset} links={links} />
          <AssetReviewOverview asset={asset} links={links} page />
        </div>
      ) : (
        <EmptyState title="Nothing to review">{asset.status === 'ready' ? 'This kind of file cannot be shared for review.' : 'The file can be reviewed once it has been processed.'}</EmptyState>
      )}
      {sharing ? <ShareAssetDialog asset={asset} open onClose={() => setSharing(false)} /> : null}
    </div>
  );
}
